import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, notifyStockChanged } from "@/lib/api";

export const ReasonDialog = ({ open, onOpenChange, title, description, confirmLabel, onConfirm, children, testid }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const go = async () => {
    if (busy) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        {children}
        <div className="space-y-1.5"><Label>Reason (required)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`} /></div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Back</Button>
          <Button disabled={!reason.trim() || busy} onClick={go} data-testid={`${testid}-confirm`}>{busy ? "Saving…" : confirmLabel}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReviseDialog = ({ open, onOpenChange, order, onDone }) => {
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && order) { setQty(Object.fromEntries(order.items.map((i) => [i.product_id, String(i.qty)]))); setReason(""); } }, [open, order]);
  if (!order) return null;
  const rows = order.items.map((i) => {
    const v = /^\d+$/.test(qty[i.product_id] || "") ? Number(qty[i.product_id]) : NaN;
    const floor = (i.received || 0) + (i.short_closed_qty || 0);
    const err = Number.isNaN(v) || v < 1 ? "Whole number, 1 or more" : v < floor ? `Can't be below received + short closed (${floor})` : "";
    return { i, v, err, changed: !err && v !== i.qty };
  });
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${order.id}/revise`, { expected_rev: order.rev || 0, reason, lines: rows.filter((r) => r.changed).map((r) => ({ product_id: r.i.product_id, ordered: r.v })) });
      toast.success("Ordered quantity updated");
      notifyStockChanged(); onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="revise-dialog">
        <DialogHeader><DialogTitle>Revise ordered qty · {order.order_no}</DialogTitle><DialogDescription>Stock does not change. Original and revised quantities are kept in the history.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {rows.map(({ i, err }, idx) => (
            <div key={i.product_id} className="flex items-start gap-3 border-b border-border/60 pb-2">
              <div className="flex-1 min-w-0 text-sm"><div className="product-name">{i.name}</div><div className="text-[11px] text-muted-foreground">received {i.received || 0}{i.short_closed_qty ? ` · short closed ${i.short_closed_qty}` : ""}</div>{err && <div className="text-[11px] text-red-500">{err}</div>}</div>
              <Input inputMode="numeric" value={qty[i.product_id] || ""} onChange={(e) => setQty((s) => ({ ...s, [i.product_id]: e.target.value }))} className="h-9 w-24 font-mono" data-testid={`revise-qty-${idx}`} />
            </div>))}
          <div className="space-y-1.5"><Label>Reason / note</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="revise-reason" /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy || rows.some((r) => r.err) || !rows.some((r) => r.changed)} onClick={save} data-testid="revise-save-btn">{busy ? "Saving…" : "Save ordered qty"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
